import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Eye, Key, UserCheck, UserX, MoreVertical } from 'lucide-react';
import { DataTable, Column } from '../../components/DataTable';
import { userManagementApi, roleApi } from '../../services/api';
import { User, Role } from '../../types';
import UserForm from './components/UserForm';
import UserDetailsPanel from './components/UserDetailsPanel';
import Badge from '../../components/ui/Badge';
import { userStatusVariant } from '../../utils/badgeVariants';

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 15,
    total: 0,
    lastPage: 1,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '' as '' | 'active' | 'inactive',
    role_id: '' as '' | number,
    sort_by: 'created_at',
    sort_order: 'desc' as 'asc' | 'desc',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.role_id) params.role_id = filters.role_id;

      const response = await userManagementApi.list(params);
      setUsers(response.data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.current_page,
        perPage: response.per_page,
        total: response.total,
        lastPage: response.last_page,
      }));
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.perPage, filters.search, filters.status, filters.role_id, filters.sort_by, filters.sort_order]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const response = await roleApi.list({ per_page: 100 });
        setRoles(response.data);
      } catch (error) {
        console.error('Failed to load roles:', error);
      }
    };
    loadRoles();
  }, []);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination((prev) => ({ ...prev, perPage, currentPage: 1 }));
  };

  const handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
  };

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilter = (status: '' | 'active' | 'inactive') => {
    setFilters((prev) => ({ ...prev, status }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleRoleFilter = (roleId: '' | number) => {
    setFilters((prev) => ({ ...prev, role_id: roleId }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await userManagementApi.delete(userId);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user');
    }
  };

  const handleToggleStatus = async (userId: number, currentStatus: 'active' | 'inactive') => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await userManagementApi.updateStatus(userId, newStatus);
      loadUsers();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update user status');
    }
  };

  const handleResetPassword = async (userId: number) => {
    if (!window.confirm('Reset password for this user? A new password will be generated.')) return;
    try {
      const response = await userManagementApi.resetPassword(userId);
      if (response.data?.new_password) {
        alert(`Password reset successfully. New password: ${response.data.new_password}`);
      } else {
        alert('Password reset successfully');
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert('Failed to reset password');
    }
  };

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (user) => (
        <div>
          <div className="font-medium theme-card-title">{user.name}</div>
          <div className="text-sm theme-card-subtitle">{user.email}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (user) => (
        <Badge variant={userStatusVariant(user.status)}>{user.status || 'active'}</Badge>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (user) => (
        <div className="flex flex-wrap gap-1">
          {user.roles && user.roles.length > 0 ? (
            user.roles.map((role) => (
              <span
                key={role.id}
                className="px-2 py-1 text-sm bg-blue-600 text-white rounded-sm"
              >
                {role.name}
              </span>
            ))
          ) : (
            <span className="text-sm theme-text-tertiary">No roles</span>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (user) => (
        <span className="text-sm theme-card-subtitle">
          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
        </span>
      ),
    },
  ];

  return (
    <div className="theme-card rounded-2xl p-3 md:p-4 relative border theme-border">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold theme-card-title">User Management</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-shadow text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Create User
        </button>
      </div>

      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onSort={handleSort}
        sortBy={filters.sort_by}
        sortOrder={filters.sort_order}
        searchable
        onSearch={handleSearch}
        searchPlaceholder="Search users by name or email..."
        filters={
          <>
            <select
              value={filters.status}
              onChange={(e) => handleStatusFilter(e.target.value as '' | 'active' | 'inactive')}
              className="border border-white px-2 py-1 theme-border rounded-lg p-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-0 theme-bg-primary theme-text-primary">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={filters.role_id}
              onChange={(e) => handleRoleFilter(e.target.value ? Number(e.target.value) : '')}
              className="border border-white px-2 py-1 theme-border rounded-lg p-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-0 theme-bg-primary theme-text-primary"
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </>
        }
        actions={(user) => (
          <div className="flex items-center gap-1.5 justify-end">
            <button
              onClick={() => setViewingUser(user)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="View Details"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setEditingUser(user)}
              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
              title="Edit"
            >
              <Edit className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleToggleStatus(user.id, user.status || 'active')}
              className={`p-1.5 rounded transition-colors ${user.status === 'active'
                  ? 'text-orange-600 hover:bg-orange-50'
                  : 'text-green-600 hover:bg-green-50'
                }`}
              title={user.status === 'active' ? 'Deactivate' : 'Activate'}
            >
              {user.status === 'active' ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={() => handleResetPassword(user.id)}
              className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
              title="Reset Password"
            >
              <Key className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDelete(user.id)}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        emptyMessage="No users found"
      />

      {showCreateModal && (
        <UserForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadUsers();
          }}
          roles={roles}
        />
      )}

      {editingUser && (
        <UserForm
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            loadUsers();
          }}
          roles={roles}
        />
      )}

      {viewingUser && (
        <UserDetailsPanel
          userId={viewingUser.id}
          onClose={() => setViewingUser(null)}
          onUpdate={loadUsers}
        />
      )}
    </div>
  );
};

export default UserManagementPage;

